import { useCallback, useState } from "react";
import { minuteMs } from "verdi-common/timeConstants";
import { MasterIndex } from "verdi-web-client/Models";

/** Patch to correct for firmware issue that should be resolved ASAP. Do not keep this past July 1, 2026. */
const END_TIME_BACKDATE_MS = minuteMs * 3;

interface DeleteZoneEventParams {
    eventId: string;
}

export const useDeleteZoneEvent = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const deleteZoneEvent = useCallback(async (deleteZoneEventParams: DeleteZoneEventParams) => {
        setLoading(true);
        setError(null);
        const now = Date.now();
        try {
            const repeatingIrrigationEvent = MasterIndex.repeatingEvent.byID[deleteZoneEventParams.eventId];
            if (!repeatingIrrigationEvent) {
                setError(new Error("Event not found"));
                return null;
            }

            // Cannot delete event that has ended
            if (repeatingIrrigationEvent.startTime.getTime() + repeatingIrrigationEvent.duration < now) {
                setError(new Error("Cannot delete event that has ended"));
                return null;
            }

            /**
             * Patch to correct for firmware issue that should be resolved ASAP. Do not keep this past July 1, 2026.
             * Events that started at least 2 minutes ago are "deleted" by setting the end time to 1 minute before now.
             * Events that started within the last 2 minutes are deleted outright to avoid leaving sub-minute events
             * lying around and to prevent the backdated end time from falling before the start time.
             */
            if (repeatingIrrigationEvent.startTime.getTime() + minuteMs + END_TIME_BACKDATE_MS <= now) {
                const newDuration = now - END_TIME_BACKDATE_MS - repeatingIrrigationEvent.startTime.getTime();
                await repeatingIrrigationEvent.edit(undefined, newDuration);
            }
            // If event has not started yet, just delete the event
            else {
                await repeatingIrrigationEvent.delete();
            }

            return true;
        } catch (errorEvent: unknown) {
            setError(errorEvent as Error);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        deleteZoneEvent,
        loading,
        error,
    };
};
